import type { ThingTransitionListener } from "./world-things.ts";
import type { SessionParts } from "./session-types.ts";

/**
 * The sounds the world's changes make, as listeners made once for a session: a thing's change of state, a shot's
 * landing and a shrine's pop.
 */
export interface WorldSounds {
  /**
   * Sounds a thing's change of state the page saw (ADR-085's amendment for P1.9-T79), handing the cue the thing and
   * the page's character as the view holds them, which places it (`SoundCues.thingSeen`): the thing's handle, its
   * appearance (undefined when the table lacks it), and the state indexes it left and took.
   */
  readonly thingChanged: ThingTransitionListener;
  /**
   * Sounds a shot landing where the page drew its burst (P2.8-T84), placed from the page's character as the view holds
   * it (`SoundCues.projectileLanded`): the ability's number and the landing point across and down, in sixteenths.
   */
  readonly projectileLanded: (ability: number, x: number, y: number) => void;
  /**
   * Sounds a shrine's pop an arrival's event started (ADR-096 decision 28), where the shrine stands: the shrine's
   * handle.
   */
  readonly thingPulsed: (handle: number) => void;
}

/**
 * Makes the listeners that sound what the world shows, each placed from the page's character as the view holds it.
 * A session without sounds makes listeners that never sound, and the welcome does not install them.
 *
 * @param parts The session's parts.
 * @returns The listeners, made once.
 */
export function worldSounds(parts: SessionParts): WorldSounds {
  const { options, state, connection } = parts;
  return {
    thingChanged: (handle, look, from, to) => {
      const entities = connection()?.view.entities;
      options.sounds?.thingSeen(entities?.get(handle), entities?.get(state.handle), look, from, to, handle);
    },
    projectileLanded: (ability, x, y) => {
      options.sounds?.projectileLanded(ability, x, y, connection()?.view.entities.get(state.handle));
    },
    thingPulsed: (handle) => {
      const entities = connection()?.view.entities;
      options.sounds?.thingEventSeen(entities?.get(handle), entities?.get(state.handle), "pulse", handle);
    },
  };
}
